import React, { useState } from 'react';
import Button from '../../common/Button';
import { jobAPI } from '../../../services/api';

// Send the customer a link to approve or decline this estimate on their phone.
// The raw url exists only in the create response (the server stores a hash), so
// "Copy a new link" really does make a new link; the old one stops working.

const errorText = (e) => e?.response?.data?.detail || e?.message || 'Something went wrong.';

// An approval's decided_at is the customer's wall clock with no offset, which
// Date reads as local time, so it shows as-is; other times carry +00:00.
const when = (iso) =>
  iso ? new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '';

const stateLine = (link) => {
  switch (link.state) {
    case 'approved':
      return `Approved by customer — ${[link.approved_by_name, when(link.decided_at)].filter(Boolean).join(', ')}`;
    case 'changed':
      return 'Estimate changed since the customer approved — send or copy a new link';
    case 'declined':
      return link.decline_reason ? `Declined by customer — ${link.decline_reason}` : 'Declined by customer';
    case 'opened':
      return 'Opened by customer';
    default:
      return 'Link created';
  }
};

const ShareLinkPanel = ({ jobId, shareLink, customerEmail, onChange }) => {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [url, setUrl] = useState('');
  const [copyStatus, setCopyStatus] = useState('');

  const copy = async (text) => {
    setCopyStatus('');
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus('Copied');
    } catch {
      // Clipboard unavailable or denied: the read-only input stays as the fallback.
      setCopyStatus('Select the link and copy it manually');
    }
  };

  const create = async (sendEmail, key) => {
    setBusy(key);
    setError('');
    setCopyStatus('');
    try {
      const result = await jobAPI.createShareLink(jobId, { send_email: sendEmail });
      setUrl(result.url || '');
      onChange(result.share_link);
      if (result.email_sent === false) {
        setError(result.email_error || `Email didn't send to ${customerEmail}`);
      } else if (!sendEmail && result.url) {
        await copy(result.url);
      }
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy('');
    }
  };

  const turnOff = async () => {
    setBusy('off');
    setError('');
    try {
      await jobAPI.revokeShareLink(jobId);
      setUrl('');
      setCopyStatus('');
      onChange(null);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy('');
    }
  };

  const noEmail = !customerEmail;

  return (
    <div className="mb-6 border border-border-default rounded-md p-4">
      <h4 className="font-semibold text-ink mb-2">Customer approval link</h4>

      {shareLink && (
        <div className="mb-3">
          <p className="text-sm font-medium text-ink">{stateLine(shareLink)}</p>
          {shareLink.sent_to_email && (
            <p className="text-small text-ink-muted">Sent to {shareLink.sent_to_email}</p>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {shareLink ? (
          <>
            <Button variant="secondary" disabled={noEmail || !!busy} loading={busy === 'send'}
              aria-describedby={noEmail ? 'share-link-no-email' : undefined}
              onClick={() => create(true, 'send')}>Resend</Button>
            <Button variant="secondary" disabled={!!busy} loading={busy === 'copy'}
              onClick={() => create(false, 'copy')}>Copy a new link</Button>
            <Button variant="danger" disabled={!!busy} loading={busy === 'off'}
              onClick={turnOff}>Turn off link</Button>
          </>
        ) : (
          <>
            <Button disabled={noEmail || !!busy} loading={busy === 'send'}
              aria-describedby={noEmail ? 'share-link-no-email' : undefined}
              onClick={() => create(true, 'send')}>Send approval link</Button>
            <Button variant="secondary" disabled={!!busy} loading={busy === 'copy'}
              onClick={() => create(false, 'copy')}>Copy link</Button>
          </>
        )}
      </div>

      {noEmail && (
        <p id="share-link-no-email" className="text-small text-ink-muted mt-2">Add the customer's email to the job</p>
      )}

      {url && (
        <div className="mt-3 flex gap-2 items-center">
          <input
            readOnly
            value={url}
            aria-label="Approval link"
            onFocus={(e) => e.target.select()}
            className="flex-1 min-h-touch border border-border-strong rounded-md px-3 py-2 text-sm"
          />
          <Button variant="secondary" onClick={() => copy(url)}>Copy link</Button>
        </div>
      )}

      <span role="status" aria-live="polite" className="block text-small text-ink-muted mt-1">
        {copyStatus}
      </span>

      {error && <p role="alert" className="text-sm text-danger mt-2">{error}</p>}

      <p className="text-small text-ink-muted opacity-60 mt-3">
        Text message — available after carrier registration
      </p>
    </div>
  );
};

export default ShareLinkPanel;
